import { z } from "zod/v3";

export const PRACTICE_APPROACH_KEYS = [
  "kbt",
  "rebt",
  "gestalt",
  "act",
  "cft",
  "dbt",
  "psychodynamic",
  "integrative",
  "systemic",
  "emdr",
  "mindfulness",
  "psychoanalysis",
  "transactional_analysis",
  "person_centered",
  "schema",
  "erp",
  "existential",
  "constructivist",
  "psychodrama",
  "body_psychotherapy",
  "emotion_focused",
  "group_analysis",
  "integrative_art",
  "integrative_child",
  "eclectic",
  "jungian",
  "logotherapy",
  "narrative",
  "solution_focused",
  "symboldrama",
  "integral_causal",
  "hypnotherapy",
  "dcr_regression",
] as const;
export const approachKeySchema = z.enum(PRACTICE_APPROACH_KEYS);
export const trainingStatusSchema = z.enum([
  "COMPLETED",
  "IN_TRAINING",
  "UNKNOWN",
]);
export const concernKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{1,79}$/)
  .refine(
    (key) => !["none", "unclear"].includes(key),
    "Reserved specialty key",
  );
export const practiceProfileInputSchema = z
  .object({
    concerns: z
      .array(
        z
          .object({
            concernKey: concernKeySchema,
            tier: z.enum(["PRIMARY", "ADDITIONAL"]),
          })
          .strict(),
      )
      .max(100),
    approaches: z
      .array(z.object({ key: approachKeySchema }).strict())
      .max(PRACTICE_APPROACH_KEYS.length),
    languageCodes: z
      .array(z.string().regex(/^[a-z]{2,3}(?:-[A-Z]{2})?$/))
      .max(50),
    qualifications: z
      .array(
        z.object({
          originalText: z.string().min(1).max(4000),
          category: z.string().max(100).nullable().optional(),
          approachKey: approachKeySchema.nullable().optional(),
          trainingStatus: trainingStatusSchema,
        }),
      )
      .max(100),
  })
  .superRefine((value, ctx) => {
    if (value.concerns.filter((item) => item.tier === "PRIMARY").length > 5)
      ctx.addIssue({
        code: "custom",
        path: ["concerns"],
        message: "Choose at most five primary specialties",
      });
    if (
      new Set(value.approaches.map((item) => item.key)).size !==
      value.approaches.length
    )
      ctx.addIssue({
        code: "custom",
        path: ["approaches"],
        message: "Duplicate approach",
      });
  });
export type PracticeProfileInput = z.infer<typeof practiceProfileInputSchema>;
export type SpecialtyCatalogEntry = {
  key: string;
  label: string;
  definition: string;
  aliases: string[];
  examples?: string[];
};
export function normalizeSpecialtyTerm(value: string) {
  return value
    .normalize("NFKC")
    .trim()
    .toLocaleLowerCase("sr-Latn")
    .replace(/\s+/g, " ");
}
export function formatPracticeLabel(label: string) {
  return label.replace(/\p{L}/u, (letter) => letter.toLocaleUpperCase("sr"));
}
export function uniquePracticeConcerns(
  concerns: PracticeProfileInput["concerns"],
) {
  const unique = new Map<string, PracticeProfileInput["concerns"][number]>();
  for (const concern of concerns) {
    const previous = unique.get(concern.concernKey);
    if (!previous) unique.set(concern.concernKey, { ...concern });
    else if (concern.tier === "PRIMARY") previous.tier = "PRIMARY";
  }
  return [...unique.values()];
}
export function canonicalConcernTiers(
  concerns: PracticeProfileInput["concerns"],
) {
  const tiers = new Map<string, "PRIMARY" | "ADDITIONAL">();
  for (const concern of concerns)
    if (concern.tier === "PRIMARY" || !tiers.has(concern.concernKey))
      tiers.set(concern.concernKey, concern.tier);
  return tiers;
}
export const APPROACH_OPTIONS = PRACTICE_APPROACH_KEYS.map((key) => ({
  key,
  label: {
    kbt: "Kognitivno-bihejvioralna terapija",
    rebt: "REBT",
    gestalt: "Geštalt",
    act: "ACT",
    cft: "Terapija usmerena na saosećanje",
    dbt: "DBT",
    psychodynamic: "Psihodinamska terapija",
    integrative: "Integrativna terapija",
    systemic: "Sistemska porodična terapija",
    emdr: "EMDR",
    mindfulness: "Mindfulness",
    psychoanalysis: "Psihoanaliza",
    transactional_analysis: "Transakciona analiza",
    person_centered: "Terapija usmerena na osobu",
    schema: "Shema terapija",
    erp: "Izlaganje i prevencija odgovora (ERP)",
    existential: "Egzistencijalna terapija",
    constructivist: "Konstruktivistička psihoterapija",
    psychodrama: "Psihodrama",
    body_psychotherapy: "Telesna psihoterapija",
    emotion_focused: "Emocionalno fokusirana terapija parova",
    group_analysis: "Grupna analiza",
    integrative_art: "Integrativna art psihoterapija",
    integrative_child: "Integrativna dečja psihoterapija",
    eclectic: "Eklektička psihoterapija",
    jungian: "Jungovska analitička psihoterapija",
    narrative: "Narativna terapija",
    logotherapy: "Logoterapija",
    solution_focused: "Terapija usmerena na rešenje",
    symboldrama: "Simboldrama",
    integral_causal: "Integralna kauzalna psihoterapija",
    hypnotherapy: "Hipnoterapija",
    dcr_regression: "DCR regresoterapija",
  }[key],
}));
const EXISTING_LANGUAGE_OPTIONS = [
  { key: "sr", label: "Srpski" },
  { key: "en", label: "Engleski" },
  { key: "bs", label: "Bosanski" },
  { key: "hr", label: "Hrvatski" },
  { key: "cnr", label: "Crnogorski" },
  { key: "de", label: "Nemački" },
  { key: "fr", label: "Francuski" },
  { key: "it", label: "Italijanski" },
  { key: "es", label: "Španski" },
  { key: "ru", label: "Ruski" },
  { key: "hu", label: "Mađarski" },
  { key: "mk", label: "Makedonski" },
  { key: "sl", label: "Slovenački" },
];
// ISO 639-1 codes are stable identities; keep existing labels and display order.
const ISO_639_1_CODES =
  "aa ab ae af ak am an ar as av ay az ba be bg bh bi bm bn bo br ca ce ch co cr cs cu cv cy da de dv dz ee el en eo es et eu fa ff fi fj fo fr fy ga gd gl gn gu gv ha he hi ho hr ht hu hy hz ia id ie ig ii ik io is it iu ja jv ka kg ki kj kk kl km kn ko kr ks ku kv kw ky la lb lg li ln lo lt lu lv mg mh mi mk ml mn mr ms mt my na nb nd ne ng nl nn no nr nv ny oc oj om or os pa pi pl ps pt qu rm rn ro ru rw sa sc sd se sg si sk sl sm sn so sq sr ss st su sv sw ta te tg th ti tk tl tn to tr ts tt tw ty ug uk ur uz ve vi vo wa wo xh yi yo za zh zu".split(
    " ",
  );
const serbianLanguageNames = new Intl.DisplayNames(["sr-Latn"], {
  type: "language",
});
const englishLanguageNames = new Intl.DisplayNames(["en"], {
  type: "language",
});
export const LANGUAGE_OPTIONS = [
  ...EXISTING_LANGUAGE_OPTIONS,
  ...ISO_639_1_CODES.filter(
    (key) => !EXISTING_LANGUAGE_OPTIONS.some((option) => option.key === key),
  )
    .map((key) => {
      const label =
        (key === "tw" ? "Tvi" : serbianLanguageNames.of(key)) ??
        englishLanguageNames.of(key) ??
        key;
      return {
        key,
        label: label.charAt(0).toLocaleUpperCase("sr-Latn") + label.slice(1),
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label, "sr-Latn")),
].map((option) => ({
  ...option,
  aliases: [
    option.key === "tw"
      ? "Twi"
      : (englishLanguageNames.of(option.key) ?? option.key),
  ],
}));
